import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { of, Subject } from 'rxjs';
import { Store } from '@ngrx/store';

import * as EbuOrderingActions from './../actions/ebu-ordering.actions';
import {
  OrderEBUOptionConfirmFailure,
  OrderEBUOptionConfirmSuccess,
  OrderEBUOptionSuccess,
  OrderEBUOptionValidationRequired,
  OrderEBUValidateFailure,
  OrderEBUValidateSuccess,
} from '../actions/ebu-ordering.actions';
import { UpdateEBUOptionActiveStatus } from '../actions/product-details.actions';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { LoggerService } from 'src/app/services/logger.service';
import {
  ApplicationState,
  getEbuOrderingLockTime,
  getSelectedProductDetails,
} from '../reducers';

import {
  buildCancelled,
  buildDefaultError,
  buildDefaultSuccess,
  extractOrderingInput,
  getOrderingInput,
  getProductUuid,
  hasIncompatibleOptions,
  isOSDOption,
} from 'src/app/shared/ebu-order-utils/ebu-order-utils';
import { getOption, Option } from '@dch/products-business-logic';
import { TranslationService } from '@proximus/ng-common';
import { ApolloRunnerService } from 'src/app/services/apollo-runner.service';
import {
  ORDER_EBU_MUTATION,
  ORDER_EBU_SUBSCRIPTION,
} from 'src/app/services/gql/ebu-option-ordering.gql';

@Injectable({
  providedIn: 'root',
})
export class EbuOrderingEffects {
  constructor(
    private actions$: Actions,
    private store: Store<ApplicationState>,
    private loggerService: LoggerService,
    private translationService: TranslationService,
    private apolloRunnerService: ApolloRunnerService,
  ) {}

  @Effect()
  ebuRequest$ = this.actions$.pipe(
    ofType(EbuOrderingActions.ORDER_EBU_REQUEST),
    mergeMap(({ optionId }) => {
      this.loggerService.logEpicCall('orderEBUOptionQuery');
      return of(new OrderEBUOptionSuccess(optionId));
    }),
  );

  @Effect()
  ebuConfimRequest$ = this.actions$.pipe(
    ofType(EbuOrderingActions.ORDER_EBU_CONFIRM_REQUEST),
    map((action: EbuOrderingActions.OrderEBUOptionConfirm) => action.payload),
    withLatestFrom(
      this.store.select(getEbuOrderingLockTime),
      this.store.select(getSelectedProductDetails),
    ),
    mergeMap(
      ([
        { productId, optionId, cancel },
        ebuOrderingLockTime,
        productDetails,
      ]) => {
        const epicCall = 'orderEBUOptionQuery';
        this.loggerService.logEpicCall(epicCall);
        if (cancel) {
          return this.loggerService.logEpicCallSuccessful(epicCall, [
            new OrderEBUOptionConfirmSuccess({
              ...buildCancelled(),
              ebuOrderingLockTime,
            }),
          ]);
        }

        const productInStore = productDetails.mobileProductDetails;

        if (getProductUuid(productInStore) === productId) {
          const option = getOption(productInStore.options, optionId);
          if (option) {
            const orderingInput = getOrderingInput(
              productInStore,
              option,
              this.translationService,
            );
            if (hasIncompatibleOptions(orderingInput)) {
              this.loggerService.log(
                'WS - Apollo - validationRequired - hasIncompatibleOptions ',
              );
              return of(
                new OrderEBUOptionValidationRequired({
                  orderingInput,
                  productId,
                  optionId,
                }),
              );
            }
            return this.orderEBUOptionApolloQuery(
              extractOrderingInput(orderingInput),
              epicCall,
              productId,
              option,
              true,
              ebuOrderingLockTime,
            );
          }

          return this.loggerService.logEpicCallError(
            epicCall,
            [
              new OrderEBUOptionConfirmFailure(
                buildDefaultError(this.translationService),
              ),
            ],
            'No option found for ' + optionId,
          );
        }
        return this.loggerService.logEpicCallError(
          epicCall,
          [
            new OrderEBUOptionConfirmFailure(
              buildDefaultError(this.translationService),
            ),
          ],
          'No product found for ' + productId,
        );
      },
    ),
  );

  @Effect()
  ebuValidationRequest$ = this.actions$.pipe(
    ofType(EbuOrderingActions.ORDER_EBU_VALIDATION_REQUEST),
    map(
      (action: EbuOrderingActions.OrderEBUOptionValidation) => action.payload,
    ),
    withLatestFrom(
      this.store.select(getEbuOrderingLockTime),
      this.store.select(getSelectedProductDetails),
    ),
    mergeMap(
      ([
        { orderingInput, cancel, productId, optionId },
        ebuOrderingLockTime,
        productDetails,
      ]) => {
        const epicCall = 'orderEBUOptionValidateQuery';
        this.loggerService.logEpicCall(epicCall);
        if (cancel) {
          return this.loggerService.logEpicCallSuccessful(epicCall, [
            new OrderEBUValidateSuccess({
              ...buildCancelled(),
              ebuOrderingLockTime,
            }),
          ]);
        }

        const productInStore = productDetails.mobileProductDetails;
        if (getProductUuid(productInStore) === productId) {
          const option = getOption(productInStore.options, optionId);
          if (option) {
            if (orderingInput) {
              return this.orderEBUOptionApolloQuery(
                extractOrderingInput(orderingInput),
                epicCall,
                productId,
                option,
                false,
                ebuOrderingLockTime,
              );
            }
          }

          return this.loggerService.logEpicCallError(
            epicCall,
            [
              new OrderEBUValidateFailure(
                buildDefaultError(this.translationService),
              ),
            ],
            'No option found for ' + optionId,
          );
        }
        return this.loggerService.logEpicCallError(
          epicCall,
          [
            new OrderEBUValidateFailure(
              buildDefaultError(this.translationService),
            ),
          ],
          'No product found for ' + productId,
        );
      },
    ),
  );

  orderEBUOptionApolloQuery = (
    orderingInput: any,
    epicCall: string,
    productId: any,
    option: Option,
    confirm: boolean,
    ebuOrderingLockTime: number,
  ) => {
    const subject = new Subject();
    this.apolloRunnerService
      .getClient()
      .mutate({
        variables: {
          input: orderingInput,
        },
        mutation: ORDER_EBU_MUTATION,
      })
      .subscribe(
        (mutateResult: any) => {
          this.apolloRunnerService
            .getClient()
            .subscribe({
              variables: {
                commandTypes: ['orderFailed', 'orderSubmitted'],
                cartId: mutateResult?.data?.orderNative?.resource?.cartId,
                jwt: mutateResult?.context?.jwt,
              },
              query: ORDER_EBU_SUBSCRIPTION,
            })
            .subscribe(
              (subscribeResult: any) =>
                subscribeResult?.data?.onCommandCreated?.resource?.type ===
                'orderSubmitted'
                  ? subject.next(subscribeResult)
                  : subject.error(subscribeResult),
              (subscribeError: any) => subject.error(subscribeError),
            );
        },
        (error) => {
          this.loggerService.logEpicCallError(epicCall, error.toString());
          subject.error(error);
        },
      );

    return subject.pipe(
      mergeMap((success: any) => {
        this.loggerService.log('WS - Apollo - orderSubmitted : ' + success);
        const actions = [
          confirm
            ? new OrderEBUOptionConfirmSuccess({
                ...buildDefaultSuccess(option, this.translationService),
                ebuOrderingLockTime,
              })
            : new OrderEBUValidateSuccess({
                ...buildDefaultSuccess(option, this.translationService),
                ebuOrderingLockTime,
              }),
        ];
        if (!isOSDOption(option)) {
          actions.push(
            new UpdateEBUOptionActiveStatus({
              productId,
              optionId: option.optionCodeForDescription,
            }),
          );
        }
        return this.loggerService.logEpicCallSuccessful(epicCall, actions);
      }),
      catchError((error: any) => {
        this.loggerService.log('WS - Apollo - subscribe error : ' + error);
        return this.loggerService.logEpicCallError(
          epicCall,
          [
            confirm
              ? new OrderEBUOptionConfirmFailure(
                  buildDefaultError(this.translationService),
                )
              : new OrderEBUValidateFailure(
                  buildDefaultError(this.translationService),
                ),
          ],
          `WS - Apollo ${error}  during ordering`,
        );
      }),
    );
  };
}
